import { useState, useEffect } from 'react';
import Button from '../components/common/Button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { servicesService, type PublicService } from '../services/servicesService';

const ServicesPage = () => {
  const [services, setServices] = useState<PublicService[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    servicesService.getAll().then((data) => {
      setServices(data);
      setLoading(false);
    });
  }, []);

  return (
    <div>
      <SEO
        title="Our Services - Cloud, DevOps & IT Solutions"
        description="Comprehensive technology services including IT infrastructure, multi-cloud solutions (AWS, Azure, GCP), DevOps implementation, Atlassian services, bespoke software development, and digital advertising."
        keywords="IT services, cloud infrastructure, AWS, Azure, GCP, DevOps, CI/CD, Atlassian, Jira, Confluence, software development, digital advertising, IT support South Africa"
        url="https://kommonview.com/services"
      />

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-aos="slide-right">
              Our Services
            </h1>
            <p className="text-xl text-gray-100" data-aos="slide-left" data-aos-delay="100">
              Enterprise-grade cloud infrastructure, DevOps automation, and bespoke software solutions engineered for scale, security, and performance
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white border-2 border-gray-200 rounded-xl p-8 animate-pulse">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl mb-6" />
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-6" />
                  <div className="space-y-2">
                    {Array.from({ length: 4 }).map((_, j) => (
                      <div key={j} className="h-4 bg-gray-200 rounded w-full" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : services.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No services available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-white border-2 border-gray-200 rounded-xl p-8 hover:border-primary hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                    {service.imageUrl ? (
                      <img
                        src={service.imageUrl}
                        alt={service.title}
                        className="w-10 h-10 object-contain group-hover:brightness-0 group-hover:invert transition-all"
                      />
                    ) : (
                      <i className={`${service.iconBrand ? 'fab' : 'fas'} ${service.icon} text-3xl text-primary group-hover:text-white transition-colors`} />
                    )}
                  </div>
                  <h3 className="text-2xl font-heading font-bold mb-4 text-gray-900">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  {service.features && service.features.length > 0 && (
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-700">
                          <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              Ready to Transform Your Infrastructure?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Partner with us to build secure, scalable, and modern cloud infrastructure that drives your business forward
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="white" size="lg">
                  Get Started
                </Button>
              </Link>
              <a href="https://kommonview.atlassian.net/servicedesk/customer/portal/1" target="_blank" rel="noopener noreferrer">
                <Button variant="whiteOutline" size="lg">
                  Support Portal
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
